import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import Navbar from "../Components/Navbar";
import logo from "../Assets/logo.png";
import useCart from "../hooks/useCart";

const Cart = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    cartItems,
    handleQuantityChange,
    removeItems,
    popupMessage,
    closePopup,
    isRemoving,

  } = useCart();

  const [isEditing, setIsEditing] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
const [isLoading, setIsLoading] = useState(false);

  const buyNowProduct = location.state?.buyNowProduct;

  useEffect(() => {
    if (buyNowProduct) {
      const isProductInCart = cartItems.some((item) => item.id === buyNowProduct.id);
      if (isProductInCart) {
        setSelectedItems([buyNowProduct.id]);
      }
    }
  }, [buyNowProduct, cartItems]);

  const handleSelectAll = () => {
    if (selectedItems.length === cartItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(cartItems.map((item) => item.id));
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((itemId) => itemId !== id)
        : [...prevSelected, id]
    );
  };

  const handleRemoveItems = () => {
    removeItems(selectedItems);
    setSelectedItems([]);
  };

  const handleEditClick = () => {
    setIsEditing(!isEditing);
  };

  const handleCheckout = async () => {
  setIsLoading(true); // Start loading
  try {
    const selectedProducts = cartItems
      .filter((item) => selectedItems.includes(item.id))
      .map((item) => ({
        id: item.id,
        name: item.product_name,
        price: item.price,
        quantity: item.quantity,
        image: item.image,
      }));

    if (selectedProducts.length === 0) {
      alert("Please select at least one product to proceed.");
      setIsLoading(false);
      return;
    }

    const totalPrice = selectedProducts.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );

    // Navigate to order summary without syncing quantities to the server
    navigate("/ordersummary", { state: { selectedProducts, totalPrice } });
  } catch (error) {
    console.error("Checkout error:", error);
    alert("An error occurred during checkout. Please try again.");
  } finally {
    setIsLoading(false); // End loading
  }
};

  return (
    <>
      <StickyContainer>
        <Navbar activeSection="marketplace" />
        <TitleContainer>
          <Title>Shopping Cart ({cartItems.length})</Title>
        </TitleContainer>
      </StickyContainer>

      <CartContainer>
        <CartHeader>
          <HeaderTop>
            <EditButton onClick={handleEditClick}>
              {isEditing ? "Done" : "Edit"}
            </EditButton>
          </HeaderTop>
        </CartHeader>

        <CartItems>
          {cartItems.length === 0 ? (
            <p style={{ textAlign: "center", fontSize: "18px" }}>
              Your cart is empty.
            </p>
          ) : (
            cartItems.map((item) => (
              <CartItem key={item.id}>
                <LeftSection>
                  <ItemCheckbox
                    type="checkbox"
                    checked={selectedItems.includes(item.id)}
                    onChange={() => handleSelectItem(item.id)}
                  />
                  <ItemImage>
                    <img
                      src={item.image ? `data:image/jpeg;base64,${item.image}` : logo}
                      alt={item.product_name}
                      onError={(e) => (e.target.src = logo)}
                    />
                  </ItemImage>
                  <ItemContent>
                    <LogoBrand>
                      <Logo src={logo} alt="Brand Logo" />
                      <BrandText>Ina-Ani</BrandText>
                    </LogoBrand>
                    <ItemDetails>
                      <h3>{item.product_name}</h3>
                      <p>₱{item.price}.00</p>
                    </ItemDetails>
                  </ItemContent>
                </LeftSection>

                <RightSection>
                  <QuantityControl>
                    <button onClick={() => handleQuantityChange(item.id, "decrement")}>-</button>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleQuantityChange(item.id, "input", e)}
                    />
                    <button onClick={() => handleQuantityChange(item.id, "increment")}>+</button>
                  </QuantityControl>
                </RightSection>
              </CartItem>
            ))
          )}
        </CartItems>

        {popupMessage && (
          <Popup>
            {popupMessage}
            <button onClick={closePopup}>Close</button>
          </Popup>
        )}

        <CartFooter>
          <SelectAllContainer>
            <SelectAllCheckbox
              type="checkbox"
              checked={selectedItems.length === cartItems.length && cartItems.length > 0}
              onChange={handleSelectAll}
            />
            <span>Select All</span>
          </SelectAllContainer>

          <FooterRight>
            <Total>
              ₱{selectedItems.length > 0
                ? selectedItems
                    .reduce((sum, id) => {
                      const item = cartItems.find((item) => item.id === id);
                      return item ? sum + item.price * item.quantity : sum;
                    }, 0)
                    .toLocaleString()
                : "0.00"}
            </Total>
            {isEditing ? (
              <CheckoutButton
                onClick={handleRemoveItems}
                disabled={selectedItems.length === 0 || isRemoving}
              >
                Remove Product ({selectedItems.length})
              </CheckoutButton>
            ) : (
              <CheckoutButton 
                onClick={handleCheckout} 
                disabled={selectedItems.length === 0 || isLoading}
                loading={isLoading ? "true" : undefined}
              >
                {isLoading ? "Processing..." : `Check out (${selectedItems.length})`}
              </CheckoutButton>
            )}
          </FooterRight>
        </CartFooter>
      </CartContainer>
    </>
  );
};

// Styled Components (unchanged)
const CartContainer = styled.div`
  max-width: 1890px;
  margin: 0 auto;
  background: #eaf4e1;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  min-height: 550px;
  padding: 5px;
`;

const CartHeader = styled.div`
  padding: 10px 20px;
  border-bottom: 1px solid #e5e5e5;
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
`;

const EditButton = styled.button`
  font-size: 19px;
  color: #006400;
  background: none;
  border: none;
  cursor: pointer;
  position: absolute;
  right: 20px;
`;

const StickyContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 1000;
  background: white;
`;

const TitleContainer = styled.div`
  background: green;
  padding: 20px;
  text-align: center;
  border-radius: 0px;
  width: 97%;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h2`
  font-size: 30px;
  font-weight: bold;
  text-align: center;
  margin: 0;
  color: white;
`;

const SelectAllContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 15px;
`;

const SelectAllCheckbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: green;
`;

const CartItems = styled.div`
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CartItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-radius: 10px;
  background-color: #f5f5f5;
  border: 1px solid #ccc;
  height: 190px;
  transition: background-color 0.3s, transform 0.3s;

  &:hover {
    background-color: rgb(175, 178, 174);
    transform: scale(1.01);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    cursor: pointer;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const ItemCheckbox = styled.input`
  width: 22px;
  height: 22px;
  cursor: pointer;
  accent-color: green;
`;

const ItemImage = styled.div`
  width: 200px;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ItemDetails = styled.div`
  h3 {
    font-size: 20px;
  }
  p {
    font-size: 16px;
    color: #555;
  }
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;

  button {
    background: none;
    border: 1px solid #ccc;
    padding: 5px;
    width: 30px;
    height: 30px;
    cursor: pointer;
  }

  input {
    width: 40px;
    height: 30px;
    text-align: center;
    border: 1px solid #ccc;
  }
`;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const Total = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: green;
`;

const CheckoutButton = styled.button`
  background: green;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  /* Add loading animation */
  ${({ loading }) => loading && `
    &::after {
      content: '';
      display: inline-block;
      width: 16px;
      height: 16px;
      border: 2px solid white;
      border-radius: 50%;
      border-top-color: transparent;
      animation: spin 1s linear infinite;
      margin-left: 8px;
    }
  `}
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LogoBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const Logo = styled.img`
  width: 30px;
  height: 30px;
`;

const BrandText = styled.span`
  font-size: 14px;
  font-weight: bold;
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const CartFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-top: 1px solid #ddd;
`;

const Popup = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  text-align: center;

  button {
    margin-top: 10px;
    background: green;
    color: white;
    border: none;
    padding: 5px 10px;
    border-radius: 4px;
    cursor: pointer;
  }
`;

export default Cart;